import { useQuery } from '@tanstack/react-query'
import {
  fetchMyPnlNewEventOptions,
  fetchMyPnlNewList,
  fetchMyPnlNewMarkets,
  fetchMyPnlNewUsers,
  type MyPnlEventRow,
  type MyPnlFilter,
} from '@/api/map/myPnlNew'
import { keys } from '@/api/hooks/shared/keys'

export function useMyPnlNew(filter: MyPnlFilter & { run: number }) {
  return useQuery({
    queryKey: keys.myPnlNew({
      from: filter.from,
      to: filter.to,
      subGame: filter.subGame,
      eventId: filter.eventId ?? 'All',
      typeName: filter.typeName ?? 'All',
      page: filter.page ?? 1,
      run: filter.run,
    }),
    queryFn: () =>
      fetchMyPnlNewList({
        from: filter.from,
        to: filter.to,
        subGame: filter.subGame,
        eventId: filter.eventId,
        typeName: filter.typeName,
        page: filter.page,
      }),
    // No placeholderData: keeping prior query data across filter changes left
    // stale events on screen (API returned 1 row; UI still showed the old event).
  })
}

export function useMyPnlNewEvents(
  filter: {
    from: string
    to: string
    subGame: string
    run: number
  },
  enabled = true,
) {
  return useQuery({
    queryKey: keys.myPnlNewEvents({
      from: filter.from,
      to: filter.to,
      subGame: filter.subGame,
      run: filter.run,
    }),
    queryFn: () =>
      fetchMyPnlNewEventOptions({
        from: filter.from,
        to: filter.to,
        subGame: filter.subGame,
      }),
    enabled: enabled && filter.run > 0,
    staleTime: 30_000,
  })
}

export function useMyPnlNewMarkets(
  filter: MyPnlFilter & { run: number },
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'subGame'> | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: keys.myPnlNewMarkets({
      from: filter.from,
      to: filter.to,
      subGame: row?.subGame ?? filter.subGame,
      eventId: row?.eventId ?? row?.id ?? '',
      eventName: row?.eventName ?? '',
      typeName: filter.typeName ?? 'All',
      run: filter.run,
    }),
    queryFn: () => fetchMyPnlNewMarkets(filter, row!),
    enabled: enabled && row != null,
  })
}

export function useMyPnlNewUsers(
  filter: MyPnlFilter & { run: number },
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'subGame'> | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: keys.myPnlNewUsers({
      from: filter.from,
      to: filter.to,
      subGame: row?.subGame ?? filter.subGame,
      eventId: row?.eventId ?? row?.id ?? '',
      eventName: row?.eventName ?? '',
      typeName: filter.typeName ?? 'All',
      run: filter.run,
    }),
    queryFn: () => fetchMyPnlNewUsers(filter, row!),
    enabled: enabled && row != null,
  })
}
